import {
  earnings as earningsFixture,
  notifications as notificationsFixture,
  panelist as panelistFixture,
  profileFields as profileFieldsFixture,
  profileFieldValues as profileFieldValuesFixture,
  profile as profileFixture,
  tasks as tasksFixture,
} from "../data/mockData";
import type {
  AppNotification,
  CashoutRequestInput,
  CashoutRequestResponse,
  DeactivateAccountResponse,
  EarningsData,
  EarningsDateRange,
  EarningStatus,
  LaunchTaskResponse,
  Panelist,
  PointsBalance,
  PreTaskResponse,
  Profile,
  ProfileField,
  ProfileFieldsData,
  ProfileFieldValue,
  ProfileFieldValues,
  ProfileSection,
  ResendSignupVerificationResponse,
  SessionContext,
  SignOutResponse,
  SignupInput,
  SignupResponse,
  Task,
  UpdateProfileInput,
} from "../types";
import { ApiError } from "./errors";

interface MockRequest {
  body?: unknown;
  headers: Headers;
  method: string;
  path: string;
}

let mockPanelist = clone(panelistFixture);
let mockTasks = clone(tasksFixture);
let mockEarnings = clone(earningsFixture);
let mockProfile = clone(profileFixture);
let mockProfileFields = clone(profileFieldsFixture);
let mockNotifications = clone(notificationsFixture);
let mockProfileFieldValues = clone(profileFieldValuesFixture);
let mockSessionContext = {
  activePanelId: 1,
  sessionId: "mock-flex-session",
  profiles: [
    {
      email: mockProfile.personal.email,
      firstName: mockPanelist.firstName,
      lastName: mockPanelist.lastName,
      panelId: 1,
      panelName: "Default Panel",
    },
    {
      email: mockProfile.personal.email,
      firstName: mockPanelist.firstName,
      lastName: mockPanelist.lastName,
      panelId: 2,
      panelName: "Second Panel",
    },
  ],
} satisfies SessionContext;

export async function handleMockRequest<T>({
  body,
  method,
  path,
}: MockRequest): Promise<T> {
  await delay(1000);

  const url = new URL(path, "https://mock.researchgarden.local");
  const pathname = url.pathname;
  const normalizedMethod = method.toUpperCase();

  if (normalizedMethod === "GET" && pathname === "/panelist") {
    return clone(buildPanelist()) as T;
  }

  if (normalizedMethod === "GET" && pathname === "/balance") {
    return clone(buildBalance()) as T;
  }

  if (normalizedMethod === "POST" && pathname === "/session/context") {
    return clone(mockSessionContext) as T;
  }

  if (normalizedMethod === "PUT" && pathname === "/session/context") {
    const input = body as { panelId?: number } | null;
    if (!input || typeof input.panelId !== "number") {
      throw new ApiError("Panel id is required.", 400);
    }
    if (
      !mockSessionContext.profiles.some(
        (profile) => profile.panelId === input.panelId,
      )
    ) {
      throw new ApiError("Selected profile not found for this session.", 400);
    }
    mockSessionContext = {
      ...mockSessionContext,
      activePanelId: input.panelId,
    };
    return clone(mockSessionContext) as T;
  }

  if (normalizedMethod === "GET" && pathname === "/tasks") {
    return clone(mockTasks) as T;
  }

  const taskMatch = pathname.match(/^\/tasks\/([^/]+)$/);
  if (normalizedMethod === "GET" && taskMatch) {
    const task = findTask(taskMatch[1]);
    return clone(task) as T;
  }

  const preTaskMatch = pathname.match(/^\/tasks\/([^/]+)\/pre$/);
  if (normalizedMethod === "GET" && preTaskMatch) {
    findTask(preTaskMatch[1]);
    return clone({ questions: [] } satisfies PreTaskResponse) as T;
  }

  const launchMatch = pathname.match(/^\/tasks\/([^/]+)\/launch$/);
  if (normalizedMethod === "POST" && launchMatch) {
    findTask(launchMatch[1]);
    return clone({
      redirectUrl: "about:blank",
    } satisfies LaunchTaskResponse) as T;
  }

  if (normalizedMethod === "GET" && pathname === "/earnings") {
    const status = (url.searchParams.get("status") ?? "all") as
      | EarningStatus
      | "all";
    const dateRange = (url.searchParams.get("date_range") ??
      "all") as EarningsDateRange;
    return clone(filterEarnings(status, dateRange)) as T;
  }

  if (normalizedMethod === "GET" && pathname === "/profile") {
    return clone(buildProfile()) as T;
  }

  if (
    normalizedMethod === "GET" &&
    (pathname === "/profile/fields" || pathname === "/api/profile/fields")
  ) {
    return clone(mockProfileFields) as T;
  }

  if (normalizedMethod === "GET" && pathname === "/auth/signup/fields") {
    return clone(buildSignupFields()) as T;
  }

  if (normalizedMethod === "GET" && pathname === "/notifications/recent") {
    return clone(
      mockNotifications.slice(0, 5) satisfies AppNotification[],
    ) as T;
  }

  if (normalizedMethod === "PUT" && pathname === "/profile") {
    const input = body as UpdateProfileInput;
    if (input.profileFields) {
      mockProfileFieldValues = applyProfileFieldValues(
        mockProfileFields,
        mockProfileFieldValues,
        input.profileFields,
      );
    }
    mockProfile = {
      ...mockProfile,
      personal: {
        ...mockProfile.personal,
        email: input.email || "",
        phone: input.phone || "",
      },
      sections: buildProfileSections(mockProfileFields, mockProfileFieldValues),
    };
    return clone(buildProfile()) as T;
  }

  if (normalizedMethod === "POST" && pathname === "/cashout/request") {
    const input = body as CashoutRequestInput;
    if (mockPanelist.points < 500) {
      throw new ApiError("Minimum 500 points required before cashout.", 400);
    }
    return clone({
      // amountPts: input.amountPts,
      method: input.method,
      status: "pending",
    } satisfies CashoutRequestResponse) as T;
  }

  if (normalizedMethod === "POST" && pathname === "/auth/signup") {
    const input = body as SignupInput;
    return clone({
      email: input.email,
      status: "verified",
    } satisfies SignupResponse) as T;
  }

  if (normalizedMethod === "POST" && pathname === "/auth/verify/resend") {
    return clone({ ok: true } satisfies ResendSignupVerificationResponse) as T;
  }

  if (normalizedMethod === "POST" && pathname === "/auth/logout") {
    return clone({ redirectTo: "/" } satisfies SignOutResponse) as T;
  }

  if (normalizedMethod === "POST" && pathname === "/panelist/delete-account") {
    return clone({
      status: "pending_deletion",
    } satisfies DeactivateAccountResponse) as T;
  }

  throw new ApiError(
    `No mock handler for ${normalizedMethod} ${pathname}`,
    404,
  );
}

function buildPanelist(): Panelist {
  return {
    ...mockPanelist,
  };
}

function buildBalance(): PointsBalance {
  return {
    panelMemberId: 1,
    panelId: 1,
    earnedPts: mockEarnings.summary.totalPts,
    pendingCashoutPts: 0,
    cashedOutPts: 0,
    availableBalancePts: mockPanelist.points,
  };
}

function buildProfile(): Profile {
  return {
    ...mockProfile,
    sections: buildProfileSections(mockProfileFields, mockProfileFieldValues),
  };
}

function buildSignupFields(): ProfileFieldsData {
  const requiredKeys = new Set([
    "living_situation",
    "household_income",
    "working_status",
    "industry",
  ]);

  return {
    sections: mockProfileFields.sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        required: requiredKeys.has(field.fieldKey),
      })),
    })),
  };
}

function findTask(id: string): Task {
  const task = mockTasks.find((item) => item.uid === id);
  if (!task) throw new ApiError("Task not found.", 404);
  return task;
}

function filterEarnings(
  status: EarningStatus | "all",
  _dateRange: EarningsDateRange,
): EarningsData {
  return {
    ...mockEarnings,
    items:
      status === "all"
        ? mockEarnings.items
        : mockEarnings.items.filter((item) => item.status === status),
  };
}

function applyProfileFieldValues(
  fieldsData: ProfileFieldsData,
  currentValues: ProfileFieldValues,
  valuesByFieldKey: Record<string, ProfileFieldValue>,
): ProfileFieldValues {
  return fieldsData.sections
    .flatMap((section) => section.fields)
    .reduce<ProfileFieldValues>((values, field) => {
      const nextValue = Object.prototype.hasOwnProperty.call(
        valuesByFieldKey,
        field.fieldKey,
      )
        ? valuesByFieldKey[field.fieldKey]
        : currentValues[field.fieldKey];
      values[field.fieldKey] = normalizeFieldValue(field, nextValue);
      return values;
    }, {});
}

function normalizeFieldValue(
  field: ProfileField,
  value: ProfileFieldValue | undefined,
): ProfileFieldValue {
  if (field.type === "multiSelect" || field.type === "checkbox") {
    return Array.isArray(value)
      ? value.filter((item) => isAllowedOption(field, item))
      : [];
  }

  if (field.type === "select" || field.type === "radio") {
    return typeof value === "string" && isAllowedOption(field, value)
      ? value
      : null;
  }

  return typeof value === "string" ? value : null;
}

function buildProfileSections(
  fieldsData: ProfileFieldsData,
  valuesByFieldKey: ProfileFieldValues,
): ProfileSection[] {
  return fieldsData.sections.map((section) => ({
    fields: section.fields.map((field) => ({
      key: field.fieldKey,
      label: field.label,
      value: formatFieldValue(field, valuesByFieldKey[field.fieldKey]),
    })),
    title: section.title,
  }));
}

function formatFieldValue(
  field: ProfileField,
  value: ProfileFieldValue | undefined,
): string | null {
  const normalizedValue = normalizeFieldValue(field, value);

  if (Array.isArray(normalizedValue)) {
    if (normalizedValue.length === 0) return null;
    return normalizedValue.map((item) => optionLabel(field, item)).join(", ");
  }

  if (!normalizedValue) return null;
  return optionLabel(field, normalizedValue);
}

function optionLabel(field: ProfileField, value: string): string {
  return field.options.find((option) => option.value === value)?.label ?? value;
}

function isAllowedOption(field: ProfileField, value: string): boolean {
  return field.options.some((option) => option.value === value);
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function clone<T>(value: T): T {
  return structuredClone(value);
}
